import React, { useEffect } from 'react';
import { Form, Input, Select } from 'antd';
import EditableCard from 'components/EditableCard';
import { WarehouseType } from 'graphql/types/WarehouseType';
import { SITE_TYPE_OPTIONS } from 'graphql/types/SiteType';
import { CountryCodeAndPhoneNumber, PhoneInput } from 'components/PhoneInput';
import { GeoType } from 'graphql/types/GeoType';
import CascadingAddressSelector from 'components/CascadingAddressSelector';
import { useTranslation } from 'react-i18next';
import { useShouldUseVerticalLayout } from '../../../../utils/languageUtils';

const { Option } = Select;

interface Address1 {
  country: string;
  state: string;
  city: string;
  zipcode: string;
}

export interface WarehouseInfoFormData {
  siteId: string;
  siteName: string;
  siteType: string;
  primarySortingCode: string;
  contactName: string;
  contactPhone: string;
  addressLine1: Address1;
  addressLine2: string;
}

interface WarehouseInfoFormProps {
  data: WarehouseType;
  isEditing?: boolean;
  isNew?: boolean;
  onEdit?: () => void;
  onSave?: (data: WarehouseType) => void;
  onCancel?: () => void;
  title?: string;
  loading?: boolean;
  bordered?: boolean;
}


const WarehouseInfoForm: React.FC<WarehouseInfoFormProps> = ({
  data,
  isEditing = false,
  isNew = false,
  onEdit,
  onSave,
  onCancel,
  title = "Warehouse Info",
  loading = false,
  bordered = false,
}) => {

  const [form] = Form.useForm<WarehouseType & { phone: CountryCodeAndPhoneNumber, locationString: string}>();
  const { t } = useTranslation();
  const useVerticalLayout = useShouldUseVerticalLayout();
  const resetFormData = () => {
    // 只设置表单显示需要的字段，避免把 zipcode 列表等字段也设置进去
    form.setFieldsValue({
      id: data.id,
      name: data.name,
      siteType: data.siteType,
      sortingCode: data.sortingCode,
      contactName: data.contactName,
      extension: data.extension,
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      country: data.country || '',
      state: data.state || '',
      city: data.city || '',
      zipcode: data.zipcode || '',
      timezone: data.timezone || '',
      phone: {
        countryCode: data.countryCode || 1,
        phoneNumber: data.phoneNumber
      },
      locationString: data.location? `${data.location.latitude}, ${data.location.longitude}` : '',
    });
  };

  useEffect(() => {
    resetFormData();
  }, [data, form]);

  // 监听编辑状态变化，取消编辑时重置表单数据
  useEffect(() => {
    if (!isEditing) {
      resetFormData();
    }
  }, [isEditing, data]);

  const canEditField = (fieldName: string) => {
    if (isNew) return true;
    if (!isEditing) return false;
    // 只允许编辑Contact Name和Contact Phone相关字段
    return ['contactName', 'phone', 'extension', 'countryCode'].includes(fieldName);
  };

  const handleSave = () => {
    const parseLocation = (locationString?: string) => {
      if (!locationString) return undefined;
      const terms = locationString.split(',').map(Number);
      if (terms.length !== 2) return undefined;
      return { latitude: terms[0], longitude: terms[1] } as GeoType;
    }

    // 如果是新建模式，返回所有字段
    if (isNew) {
      const values = form.getFieldsValue();
      let newValues: WarehouseType & { phone?: CountryCodeAndPhoneNumber, locationString?: string} = { 
        ...values,
        phoneNumber: values.phone.phoneNumber,
        countryCode: values.phone.countryCode,
        location: parseLocation(values.locationString)
      };
      delete newValues.phone;
      delete newValues.locationString;
      onSave?.(newValues as WarehouseType);
      return;
    }

    // 编辑模式：只从表单中获取允许编辑的字段值
    // 使用 form.getFieldValue 逐个获取，而不是 getFieldsValue() 获取所有
    const editableValues: Partial<WarehouseType> = {
      id: data.id,  // 必须保留 id
      contactName: form.getFieldValue('contactName'),
      phoneNumber: form.getFieldValue('phone')?.phoneNumber,
      countryCode: form.getFieldValue('phone')?.countryCode,
      extension: form.getFieldValue('extension'),
      addressLine1: form.getFieldValue('addressLine1'),
      addressLine2: form.getFieldValue('addressLine2'),
      location: parseLocation(form.getFieldValue('locationString')),
    };

    onSave?.(editableValues as WarehouseType);
  };

  return (
    <EditableCard
      title={title}
      isEditing={isEditing}
      onEdit={onEdit}
      onSave={handleSave}
      onCancel={onCancel}
      loading={loading}
      bordered={bordered}
    >
      <Form
        layout={useVerticalLayout ? "vertical" : "horizontal"}
        form={form}
        colon={false}
        labelCol={useVerticalLayout ? undefined : { span: 7 }}
        wrapperCol={useVerticalLayout ? undefined : { span: 17 }}
        className="[&_.ant-form-item-control-input]:w-full [&_.ant-input]:w-full [&_.ant-select]:w-full [&_.ant-select-selector]:w-full [&_.ant-form-item-control-input-content]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item-label]:text-left [&_.ant-form-item-label>label]:text-left"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4 mb-4 [&_.ant-form-item-control-input-content]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item]:mb-0">
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.siteId')}</span>}
            name="id"
          >
            <Input
              value={data.id}
              disabled={!isNew}
            />
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.siteName')}</span>}
            name="name"
          >
            <Input
              value={data.name}
              disabled={!isNew}
            />
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.siteType')}</span>}
            name="siteType"
          >
            <Select
              value={data.siteType}
              disabled={!isNew}
              options={SITE_TYPE_OPTIONS}
            >
            </Select>
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.shortingCode')}</span>}
            name="sortingCode"
            rules={isNew ? [
              { required: true, message: 'Sorting code is required' },
              {
                pattern: /^[A-Z-]+$/,
                message: 'Only uppercase letters and hyphens (-) are allowed'
              }
            ] : []}
          >
            <Input
              value={data.sortingCode}
              disabled={!isNew}
              placeholder={isNew ? "Enter sorting code (A-Z and - only)" : undefined}
              onChange={isNew ? (e) => {
                // Convert to uppercase and filter allowed characters
                const value = e.target.value.toUpperCase().replace(/[^A-Z-]/g, '');
                form.setFieldValue('sortingCode', value);
              } : undefined}
            />
          </Form.Item>
          {(data.businessType === 1 || data.businessType === 2) && (
            <Form.Item
              label={<span className="font-bold">{t('central.sites.details.businessType')}</span>}
            >
              <Input
                value={data.businessType === 1 ? (t('central.sites.details.businessTypeSorting') as string) : data.businessType === 2 ? (t('central.sites.details.businessTypeLastMile') as string) : `${data.businessType}`}
                disabled
              />
            </Form.Item>
          )}
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.contactName')}</span>}
            name="contactName"
            rules={[{ required: true, message: 'Please enter a contact name' }]}
          >
            <Input
              value={data.contactName}
              readOnly={!canEditField('contactName')}
            />
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.contactPhone')}</span>}
            name="phone"
            rules={[
              { required: true, message: 'Please enter phone number' },
              {
                validator: (_, value) => {
                  if (!value || !value.phoneNumber) {
                    return Promise.resolve();
                  }
                  // Check if phone number contains only digits
                  if (!/^\d*$/.test(value.phoneNumber)) {
                    return Promise.reject(new Error('Only numbers are allowed'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <PhoneInput
              placeholder={t('central.sites.details.contactPhone') || undefined}
              disabled={!canEditField('phone')}
            />
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.ext')}</span>}
            name="extension"
            rules={[
              {
                pattern: /^\d*$/,
                message: 'Only numbers are allowed'
              },
              {
                validator: (_, value) => {
                  if (value && value.length > 10) {
                    return Promise.reject(new Error('Extension number is too long, maximum 10 characters'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              value={data.extension}
              readOnly={!canEditField('extension')}
              placeholder={t('central.sites.details.ext') || undefined}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/[^0-9]/g, '');
                form.setFieldValue('extension', value);
              }}
            />
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.addr1')}</span>}
            name="addressLine1"
          >
            <Input
              value={data.addressLine1}
              disabled={!isNew}
            />
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.addr2')}</span>}
            name="addressLine2"
          >
            <Input
              value={data.addressLine2}
              disabled={!isNew}
            />
          </Form.Item>
          {isNew ? (
            <Form.Item
              label={<span className="font-bold">{t('central.sites.details.address')}</span>}
              required
              className="lg:col-span-2 mb-0"
            >
              <CascadingAddressSelector
                form={form}
                countryWidth="20%"
                stateWidth="20%"
                cityWidth="30%"
                zipcodeWidth="30%"
                autoFillGeoLocation={true}
                geoLocationFieldName="locationString"
                initialCountry={data.country}
                initialState={data.state}
                initialCity={data.city}
                initialZipcode={data.zipcode}
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                label={<span className="font-bold">{t('central.sites.details.country')}</span>}
                name="country"
              >
                <Input value={data.country} disabled />
              </Form.Item>
              <Form.Item
                label={<span className="font-bold">{t('central.sites.details.state')}</span>}
                name="state"
              >
                <Input value={data.state} disabled />
              </Form.Item>
              <Form.Item
                label={<span className="font-bold">{t('central.sites.details.city')}</span>}
                name="city"
              >
                <Input value={data.city} disabled />
              </Form.Item>
              <Form.Item
                label={<span className="font-bold">{t('central.sites.details.zipcode')}</span>}
                name="zipcode"
              >
                <Input value={data.zipcode} disabled />
              </Form.Item>
            </>
          )}
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.timezone')}</span>}
            name="timezone"
          >
            <Input
              value={data.timezone}
              disabled={!isNew}
            />
          </Form.Item>
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.geoCoding')}</span>}
            name="locationString"
          >
            <Input
              placeholder={t('central.sites.placeholders.autoFilledFromZipcode') as string}
              disabled
              className="bg-gray-100"
            />
          </Form.Item>
        </div>
      </Form>
    </EditableCard>
  );
};

export default WarehouseInfoForm; 